import { memo, useMemo } from 'react';
import { Focusable } from 'react-aria-components';
import { ComponentContextProps } from 'src/components/ComponentContext';

import { Heading, IconProps, Separator, Text, Tooltip, TooltipTrigger } from '@/components/core';
import {
  CheckmarkDuotoneIcon,
  CheckmarkIcon,
  ErrorDuotoneIcon,
  InfoIcon,
  WarningDuotoneIcon,
} from '@/components/icons';
import { ConfidenceBar, Pipe } from '@/components/ui';
import { VALIDATOR_CONFIDENCE_FUNCTION_ID } from '@/constants';
import { selectFromFieldConfig, useDocumentStore } from '@/store';
import { FormatterWarningError } from '@/utils';

type WarningErrorItemProps = {
  fieldName: string;
  formatterOrValidator: FormatterWarningError;
};

const WarningErrorItem = ({ fieldName, formatterOrValidator }: WarningErrorItemProps) => {
  const config = useDocumentStore(selectFromFieldConfig(fieldName));

  const formatterOrValidatorConfig = useMemo(() => {
    const fvs = [...(config?.formatters ?? []), ...(config?.validators ?? [])];
    return fvs.find((f) => f.id === formatterOrValidator.id);
  }, [config?.formatters, config?.validators, formatterOrValidator.id]);

  if (formatterOrValidatorConfig?.functionId === VALIDATOR_CONFIDENCE_FUNCTION_ID) return null;

  const isError = (formatterOrValidator?.errors ?? []).length > 0;
  const isWarning = (formatterOrValidator?.warnings ?? []).length > 0;
  const variant = isError ? 'error' : isWarning ? 'warning' : 'success';

  return (
    <div key={formatterOrValidator.id} className="gap-md p-sm flex">
      <Pipe variant={variant} />
      <div className="flex flex-col">
        <Text className="font-semibold" slot="description">
          {formatterOrValidatorConfig?.name}
        </Text>
        {formatterOrValidator.errors.map((error, index) => (
          <Text key={`error-${index}`} slot="description">
            {error}
          </Text>
        ))}
        {formatterOrValidator.warnings.map((warning, index) => (
          <Text key={`warning-${index}`} slot="description">
            {warning}
          </Text>
        ))}
      </div>
    </div>
  );
};

export type PredictionInfoIconProps = Omit<IconProps, 'SVGComponent'> & {
  fieldName: string;
  formattedValue?: string | null;
  confidence?: number | null;
  formatters?: FormatterWarningError[];
  validators?: FormatterWarningError[];
  isTableField?: boolean;
  isTouched?: boolean;
};

const IPredictionInfoIcon = ({
  fieldName,
  formattedValue,
  confidence,
  formatters,
  validators,
  isTableField,
  isTouched,
  ...rest
}: PredictionInfoIconProps) => {
  const { variant, Icon, heading, HeaderIcon, bottomText } = useMemo(() => {
    const all = [...(formatters ?? []), ...(validators ?? [])];
    const hasWarnings = all.filter((e) => e.warnings.length > 0).length > 0;
    const hasErrors = all.filter((e) => e.errors.length > 0).length > 0;

    let variant: ComponentContextProps['variant'];
    let Icon;
    let HeaderIcon;
    let heading;
    let bottomText;
    if (hasErrors) {
      variant = 'error';
      Icon = ErrorDuotoneIcon;
      HeaderIcon = ErrorDuotoneIcon;
      heading = 'Auto validation failed';
      bottomText = 'Fix errors before confirming';
    } else if (hasWarnings && !isTouched) {
      variant = 'warning';
      Icon = WarningDuotoneIcon;
      HeaderIcon = WarningDuotoneIcon;
      heading = 'Auto validation warning';
      bottomText = 'Review values before confirming';
    } else {
      variant = 'success';
      Icon = isTableField ? CheckmarkIcon : CheckmarkDuotoneIcon;
      HeaderIcon = CheckmarkDuotoneIcon;
      heading = 'Auto validated';
      bottomText = 'Not correct?';
    }

    return { variant, Icon, heading, HeaderIcon, bottomText };
  }, [formatters, isTableField, isTouched, validators]);

  const config = useDocumentStore(selectFromFieldConfig(fieldName));

  const { confidenceThreshold, confidenceVariant, hasConfidenceValidator } = useMemo(() => {
    const confidenceValidatorConfig = config?.validators?.find(
      (v) => v.functionId === VALIDATOR_CONFIDENCE_FUNCTION_ID
    );
    const confidenceThreshold = confidenceValidatorConfig?.config?.thresholds?.automate;

    const confidenceValidator = validators?.find((fv) => fv.id === confidenceValidatorConfig?.id);
    const confidenceHasErrors = (confidenceValidator?.errors ?? []).length > 0;
    const confidenceHasWarnings = (confidenceValidator?.warnings ?? []).length > 0;
    const confidenceVariant = (
      confidenceHasErrors ? 'error' : confidenceHasWarnings ? 'warning' : 'success'
    ) as ComponentContextProps['variant'];

    return { confidenceThreshold, confidenceVariant, hasConfidenceValidator: !!confidenceValidator };
  }, [config?.validators, validators]);

  const showConfidenceBar = useMemo(() => {
    if (formattedValue == null) return false;
    if (variant !== 'success' && confidenceVariant !== 'success') return true;
    if (variant === 'success' && !isTouched) return true;
    return false;
  }, [confidenceVariant, formattedValue, isTouched, variant]);

  const showGrayIcon = useMemo(() => {
    const hasFormatterErrors = (formatters ?? []).filter((e) => e.errors.length > 0).length > 0;
    if (hasFormatterErrors) return true;
    if (!showConfidenceBar || confidence == null) {
      if (validators?.length === 0) return true;
      if (validators?.length === 1 && hasConfidenceValidator) return true;
    }
    if (variant === 'success' && isTouched) return true;
    return false;
  }, [confidence, formatters, hasConfidenceValidator, isTouched, showConfidenceBar, validators?.length, variant]);

  if (showGrayIcon) return null;

  return (
    <TooltipTrigger>
      <Tooltip variant="plain" className="max-w-[15rem] min-w-[15rem] flex-col p-0" placement="left top">
        <div className="gap-sm p-sm flex items-center">
          <HeaderIcon variant={variant} />
          <Heading size="sm">{heading}</Heading>
        </div>
        <Separator orientation="horizontal" />
        {showConfidenceBar ? (
          <ConfidenceBar variant={confidenceVariant} confidence={confidence} threshold={confidenceThreshold} />
        ) : null}
        {variant === 'success'
          ? validators
              ?.filter((v) => v.errors.length === 0 && v.warnings.length === 0)
              .map((f) => <WarningErrorItem key={f.id} fieldName={fieldName} formatterOrValidator={f} />)
          : null}
        {variant !== 'success'
          ? validators
              ?.filter((v) => v.errors.length > 0 || v.warnings.length > 0)
              .map((f) => <WarningErrorItem key={f.id} fieldName={fieldName} formatterOrValidator={f} />)
          : null}
        <div className="p-sm gap-sm flex items-center rounded-b-lg border-t border-gray-200 bg-gray-50">
          <InfoIcon className="!text-gray-400" />
          <Text className="text-xs text-gray-700" slot="description">
            {bottomText}
          </Text>
          {variant === 'success' ? (
            <>
              <div className="grow" />
              <a
                className="text-xs font-semibold text-gray-700"
                href="https://docs.cradl.ai/core-concepts/ai-model/validators"
                target="_blank"
              >
                Learn more
              </a>
            </>
          ) : null}
        </div>
      </Tooltip>
      <Focusable excludeFromTabOrder>
        <Icon role="button" variant={variant} tabIndex={-1} {...rest} />
      </Focusable>
    </TooltipTrigger>
  );
};

export const PredictionInfoIcon = memo(IPredictionInfoIcon) as typeof IPredictionInfoIcon;
